// R87 — **THE ACCOUNT MENU CALLED A PAYING MEMBER "FREE" AND TOOK HER BILLING DOOR AWAY.**
//
// R81's census, walked to the row nobody had claimed: `App.tsx:504 member` and the two reads beside
// it. `/api/membership/status` is asked TWICE from App.tsx, both times as
//
//     .then((r) => (r.ok ? r.json() : null))
//     .then((d) => { if (active && d?.ok) setSubStatus(d); })
//     .catch(() => {});
//
// — no else on either, and `.catch(() => {})` on both. **THIS IS THE ONE READ ON THIS SITE THAT
// ANSWERS "WHAT HAS SHE PAID FOR", AND WHEN IT DID NOT LAND THE HOUSE ANSWERED IT ANYWAY.**
//
// **WHAT SHE ACTUALLY GOT, AND IT IS THREE CONTROLS ON ONE PANEL** (`AccountMenuPanel`, which is the
// one account control on every page of this site):
//
//   • **THE TIER LINE SAID "FREE".** `AccountSubscription` reads `status?.tierLabel || (isMember ?
//     "Member" : "Free")`, and a landed read ALWAYS carries `tierLabel` — the server sends "Free" at
//     minimum. So that fallback is reachable on exactly one condition: **the read did not land.** It
//     is not a default, it is the failure paint, and it asserts a fact about her money (R64/R70's
//     finding: not a missing sentence but a WRONG one).
//   • **THE BILLING DOOR WAS NOT THERE.** `subStatus?.canManageBilling` is false when `subStatus` is
//     null, so Manage billing simply is not rendered — the only way to her card, her invoices and
//     Cancel, gone, with no error state at all (R25's argument, which R58 measured and R85 paid for
//     twice).
//   • **AND THE ROW ABOVE IT OFFERED HER A MEMBERSHIP SHE ALREADY HOLDS.** `serverTier` stays null,
//     `null !== "vet"`, so the panel draws Upgrade — or, with `isMember` false, *Choose your
//     membership* — at a member who is on the top tier.
//
// **AND `isMember` FALLS BACK TO A COOKIE Q4 ALREADY PROVED IS A LIE.** `member.subscribed` is
// written FALSE at sign-in and never re-issued; Q4's own comment measured that on production with a
// real test-mode payment. A failed status read reinstates that defect for the whole session. **The
// access answer is deliberately NOT changed by this round** — only the server grants, and inventing
// a tier on the client is the one thing this house forbids outright. What changes is that the
// silence ends.
//
// **THE SERVER WAS FORGING IT TOO, AND THIS IS THE FIRST INSTANCE OF THAT SEAM WHERE THE FABRICATED
// FACT IS MONEY.** `handleSubscriptionStatus`'s catch answered
//
//     200 { ok: false, tier: "free", tierLabel: "Free" }
//
// — a claim about what she has paid for, manufactured by a catch block over a database that could
// not be reached. It rode beside an honest `ok: false`, so no client painted it today; that is a
// property of the callers, not of the payload, and the next caller to read `d.tier` without reading
// `d.ok` gets a downgrade invented by an outage. The tier is gone from that branch and the route
// composes its own sentence instead (R72's line: a message is the server's own word only when it
// arrives beside `ok: false`).
//
// **BOTH HALVES OF THE GUARD ARE LOAD-BEARING AND EACH IS PROBED WITH AN INPUT ONLY IT CAN REFUSE
// (R28).** `ok === false` catches the refusal; the PRESENCE of `tier` catches a 200 that carries no
// answer. And they are not redundant during a rolling deploy: an instance still running the old
// build sends `{ ok: false, tier: "free" }`, which has both a refusal flag AND a tier, and only the
// `ok === false` half refuses it.
//
// `doFetch` is a PARAMETER (R46), so every answer the route can give is reachable with no browser,
// no session and no database.
export type MembershipAnswer<T> = {
  // **THE LOAD-BEARING FIELD, AND IT IS NOT `ok`.** `known` is "we learned what she is on". Only a
  // landed read knows, and every sentence and every paint on this panel hangs off exactly this.
  known: boolean;
  kind: "ok" | "sign-in" | "refused" | "offline";
  // Meaningful ONLY when `known`. Null on every other arm because nothing was learned — never
  // because she is on nothing.
  status: T | null;
  message: string;
};

export const membershipStatusPath = "/api/membership/status";

const nothingLearned = <T,>(kind: "sign-in" | "refused" | "offline", message = ""): MembershipAnswer<T> => ({
  known: false, kind, status: null, message,
});

export async function readMembershipStatus<T>(
  doFetch: typeof fetch = typeof fetch === "function" ? fetch : (undefined as never),
): Promise<MembershipAnswer<T>> {
  let response: Response;
  try {
    // `no-store`: what she is paying is the one thing on this site a stale cache must never answer.
    response = await doFetch(membershipStatusPath, { cache: "no-store" });
  } catch {
    // R18/R63: a request that never landed is TRANSPORT NOISE, not the server's word about her money.
    return nothingLearned<T>("offline");
  }
  const data = await response.json().catch(() => null) as
    | { ok?: boolean; tier?: unknown; message?: string }
    | null;
  const message = typeof data?.message === "string" ? data.message : "";
  // **`sign-in` IS ITS OWN ARM AND IT IS ARGUED (R58's ruling), NOT COPIED.** This route reads the
  // signed session and 401s on it, so that is an answer it CAN give — and it is the one refusal here
  // she can fix in a single tap (R61). Both call sites only fire when the app believes she is signed
  // in, so a 401 means the session lapsed underneath her, which is exactly worth saying.
  if (response.status === 401) return nothingLearned<T>("sign-in", message);
  if (!response.ok || !data || data.ok === false || !("tier" in data)) {
    return nothingLearned<T>("refused", message);
  }
  return { known: true, kind: "ok", status: data as unknown as T, message: "" };
}

// **THE SENTENCE, DECIDED BY THE LAW AND NEVER AT THE CALL SITE.**
//
// **THREE VALUES, BECAUSE TWO CANNOT SAY IT** (W5-2/R39's third state): `null` is "nothing has been
// asked yet" and says nothing, a landed read says nothing, and only a read that failed has anything
// to say. A panel that flashes this while the request is in flight is a panel that accuses the house
// on every open.
//
// `refused` and `offline` take one sentence today and are still kept APART in the answer (R18): they
// are two different facts, and the day one of them earns its own words the law is where they part.
export function membershipStatusNote<T>(
  answer: MembershipAnswer<T> | null,
  copy: { signIn: string; failed: string },
) {
  if (!answer || answer.known) return "";
  return answer.kind === "sign-in" ? copy.signIn : copy.failed;
}
